import { queryOptions } from '@tanstack/react-query';
import type { PaginationState } from '@tanstack/react-table';

import { DEFAULT_PAGINATED_LIST_DTO } from '@apple/utils/pagination/models';

import { getComments, getOrderDetail, getPurchaseOrderLines } from '../api/details';
import { getPendingReplyCount, getRecentOrders, searchOrders } from '../api/search';
import type { OrderSearchFilters } from '../models/search';

export const orderQueryKeys = {
	all: ['orders'],
	search: (pagination: PaginationState, filters: OrderSearchFilters) => [
		...orderQueryKeys.all,
		pagination,
		filters,
	],
	approvals: () => [...orderQueryKeys.all, 'approvals'],
	order: (poNumber: string) => [...orderQueryKeys.all, poNumber],
	orderLines: (poNumber: string) => [...orderQueryKeys.all, poNumber, 'lines'],
	orderComments: (poNumber: string) => [...orderQueryKeys.all, poNumber, 'comments'],
	pendingReplyCount: (startDate?: Date) => [
		...orderQueryKeys.all,
		'pending-reply-count',
		startDate?.toISOString() ?? null,
	],
	recentOrders: (days: number, take: number) => [
		...orderQueryKeys.all,
		'recent-orders',
		days,
		take,
	],
} as const;

export const orderQueries = {
	search: (pagination: PaginationState, filters: OrderSearchFilters) =>
		queryOptions({
			queryKey: orderQueryKeys.search(pagination, filters),
			queryFn: async ({ signal }) => searchOrders(pagination, filters, signal),
			initialData: DEFAULT_PAGINATED_LIST_DTO,
			initialDataUpdatedAt: 0,
			throwOnError: true,
		}),
	order: (poNumber: string) =>
		queryOptions({
			queryKey: orderQueryKeys.order(poNumber),
			queryFn: async ({ signal }) => getOrderDetail(poNumber, signal),
			throwOnError: true,
		}),
	orderLines: (poNumber: string) =>
		queryOptions({
			queryKey: orderQueryKeys.orderLines(poNumber),
			queryFn: async ({ signal }) => getPurchaseOrderLines(poNumber, signal),
			throwOnError: true,
		}),
	orderComments: (poNumber: string) =>
		queryOptions({
			queryKey: orderQueryKeys.orderComments(poNumber),
			queryFn: async ({ signal }) => getComments(poNumber, signal),
			throwOnError: true,
		}),
	pendingReplyCount: (startDate?: Date) =>
		queryOptions({
			queryKey: orderQueryKeys.pendingReplyCount(startDate),
			queryFn: async ({ signal }) => getPendingReplyCount(startDate, signal),
		}),
	recentOrders: ({ days = 30, take = 20 }: { days?: number; take?: number } = {}) =>
		queryOptions({
			queryKey: orderQueryKeys.recentOrders(days, take),
			queryFn: async ({ signal }) => getRecentOrders(days, take, signal),
		}),
} as const;
